import {
	ChangeDetectionStrategy,
	Component,
	Inject,
	OnInit,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getNameFull } from '@core/util/client';
import { getEncumbranceName } from '@core/util/encumbrance';
import { getMortgageName } from '@core/util/mortgage';
import {
	BusinessDocumentV2,
	GroupType,
	FriendlyGroupTypes,
} from '@models/documents';
import { Encumbrance } from '@models/encumbrance';
import { Department, Jurisdiction } from '@models/jurisdictions';
import {
	Context,
	ContextAccessors,
	Debt,
	FriendlyContextAccessors,
} from '@models/matter';
import {
	MatterMortgage,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { NavigationService } from '@services/nav/nav.service';
import { Clients, MatterClients } from '@state/global/matter_clients';
import { DebtsState } from '@state/matter/debts';
import { MatterEncumbrancesState } from '@state/matter/encumbrances';
import { MatterJurisdictionSelectors } from '@state/matter/jurisdictions/jurisdictions.selectors';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { MatterMortgagesState } from '@state/matter/matter_mortgages';

export interface ContextDialogData {
	document: BusinessDocumentV2;
	jurisdictionDepartments: { [id: string]: Department[] };
}

export interface ContextDialogResult {
	context: Context;
	item: ContextDialogItem;
}

interface ContextDialogItem {
	id: string;
	name: string;
	subItem?: ContextDialogItem;
	disabled?: boolean;
}

@Component({
	selector: 'app-context-dialog',
	templateUrl: './context-dialog.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ContextDialogComponent implements OnInit {
	public document: BusinessDocumentV2;
	public contextAccessor: ContextAccessors;
	public subContextAccessor: ContextAccessors;
	public friendlyGroupType = FriendlyGroupTypes;
	public friendlyContextAccessors = FriendlyContextAccessors;
	public items: ContextDialogItem[] = [];
	public selectedItem: ContextDialogItem;
	public selectedSubItem: ContextDialogItem;
	public selectedContext: Context;
	public requiredSubContextText: string;
	public jurisdictionDepartments: { [id: string]: Department[] } = {};
	public unsupportedContext = false;

	@SelectSnapshot(MatterMortgagesState.getAllMortgages)
	mortgages: MatterMortgage[];
	@SelectSnapshot(MatterClients.get_clients) clients: Clients[];
	@SelectSnapshot(DebtsState.getAll) debts: Debt[];
	@SelectSnapshot(MatterEncumbrancesState.getAll) encumbrances: Encumbrance[];
	@SelectSnapshot(MatterJurisdictionSelectors.getAll)
	jurisdictions: Jurisdiction[];
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	constructor(
		public dialogRef: MatDialogRef<ContextDialogComponent>,
		public navigationService: NavigationService,
		@Inject(MAT_DIALOG_DATA) public data: ContextDialogData,
	) {
		this.document = data.document;
		this.jurisdictionDepartments = data.jurisdictionDepartments;
	}

	async ngOnInit(): Promise<void> {
		switch (this.document.groupType) {
			case GroupType.AFFIDAVIT:
				this.contextAccessor = ContextAccessors.CLIENTS;
				this.clients.forEach((client) => {
					this.items.push({
						id: client.id,
						name: getNameFull(client),
					});
				});
				break;
			case GroupType.DEBT:
				this.contextAccessor = ContextAccessors.DEBTS;
				this.debts.forEach((debt) => {
					this.items.push({
						id: debt.id,
						name: debt.payee.name,
					});
				});
				break;
			case GroupType.ENCUMBRANCE:
				this.contextAccessor = ContextAccessors.ENCUMBRANCES;
				this.encumbrances.forEach((encumbrance) => {
					this.items.push({
						id: encumbrance.id,
						name: getEncumbranceName(encumbrance),
					});
				});
				break;
			case GroupType.MORTGAGE:
				this.contextAccessor = ContextAccessors.MORTGAGES;
				this.mortgages.forEach((mortgage) => {
					this.items.push({
						id: mortgage.id,
						name: getMortgageName(mortgage as MatterMortgageWithAssociations),
					});
				});
				break;
			case GroupType.REMAIN:
				this.contextAccessor = ContextAccessors.ENCUMBRANCES;
				this.encumbrances.forEach((encumbrance) => {
					this.items.push({
						id: encumbrance.id,
						name: getEncumbranceName(encumbrance),
					});
				});
				break;
			case GroupType.JURISDICTION:
			case GroupType.JURISDICTION_TAX_DEPARTMENT:
			case GroupType.JURISDICTION_ZONING_DEPARTMENT:
			case GroupType.JURISDICTION_FIRE_DEPARTMENT:
			case GroupType.JURISDICTION_WATER_DEPARTMENT:
			case GroupType.JURISDICTION_HYDRO_DEPARTMENT:
			case GroupType.JURISDICTION_BUILDING_DEPARTMENT:
			case GroupType.JURISDICTION_CONSERVATION_AUTH_DEPARTMENT:
				this.contextAccessor = ContextAccessors.JURISDICTIONS;
				this.subContextAccessor = ContextAccessors.JURISDICTION_DEPARTMENTS;

				this.requiredSubContextText = ` with a ${this.document.groupType.toLowerCase()} department`;
				this.setJurisdictionItems(this.document.groupType);
				break;
			case GroupType.VACANT_TAX:
			default:
				this.unsupportedContext = true;
				break;
		}
	}

	public selectContextItem(item: ContextDialogItem): void {
		if (item.disabled) {
			return;
		}

		this.selectedItem = item;
		this.selectedContext = {
			[this.contextAccessor]: [{ referenceId: item.id }],
		};
		if (this.selectedItem?.subItem) {
			this.selectedSubItem = this.selectedItem.subItem;
			this.selectedContext[this.subContextAccessor] = [
				{ referenceId: this.selectedSubItem.id },
			];
		}
	}

	public continue(): void {
		this.dialogRef.close({
			context: this.selectedContext,
			item: this.selectedItem,
		});
	}

	public cancel(): void {
		this.dialogRef.close();
	}

	public addNew(): void {
		switch (this.contextAccessor) {
			case ContextAccessors.CLIENTS:
				this.navigationService.goToMatter_Parties(this.matterId);
				break;
			case ContextAccessors.DEBTS:
				this.navigationService.goToMatter_Debts(this.matterId);
				break;
			case ContextAccessors.ENCUMBRANCES:
				this.navigationService.goToMatter_Mortgages_Encumbrances(this.matterId);
				break;
			case ContextAccessors.MORTGAGES:
				this.navigationService.goToMatter_Mortgages(this.matterId);
				break;
			case ContextAccessors.JURISDICTIONS:
			case ContextAccessors.JURISDICTION_DEPARTMENTS:
				this.navigationService.goToMatter_Property_Jurisdictions(this.matterId);
				break;
		}
		this.dialogRef.close();
	}

	setJurisdictionItems(groupType: GroupType): void {
		this.jurisdictions?.forEach((jurisdiction) => {
			const newItem: ContextDialogItem = {
				id: jurisdiction.id,
				name: jurisdiction.name,
			};

			const matchedDepartment = Object.entries(
				jurisdiction?.departments ?? {},
			)?.find(
				(value) => value[1]?.type?.toLowerCase() === groupType?.toLowerCase(),
			);

			if (matchedDepartment) {
				newItem.subItem = {
					id: matchedDepartment[0],
					name: matchedDepartment[1].name,
				};

				this.items.push(newItem);
			} else {
				if (
					this.jurisdictionDepartments[jurisdiction.id]?.find(
						(department) =>
							department.type.toLowerCase() === groupType.toLowerCase(),
					)
				) {
					newItem.disabled = true;
					this.items.push(newItem);
				}
			}
		});
	}

	navigateToJurisdiction(): void {
		this.navigationService.goToMatter_Property_Jurisdictions(this.matterId);
		this.dialogRef.close();

		// if we can get the jurisdiction we can pop open the modal for them to add it in
		// this.dialogRef
		// .open(JurisdictionDialogStepperComponent, {
		//   width: '550px',
		//   data: {
		//     title: 'Add a new department',
		//     jurisdiction: jurisdiction,
		//     step: 2,
		//   },
		// })
	}
}
